import {
  Banner,
  BlockStack,
  Button,
  InlineStack,
  Layout,
  LegacyCard,
  TextField,
} from "@shopify/polaris";
import { useEffect, useState } from "react";

import {
  g_trafficarmor_settings,
  u_trafficarmor_settings,
} from "../api";
import storageUtils from "../utils/storageUtils";

const EMPTY_SETTINGS = {
  trafficarmor_campaign_id: "",
  trafficarmor_campaign_key: "",
  trafficarmor_allowed_hosts: "",
  trafficarmor_safe_page_html: "",
};

const formatSettings = (data = {}) => ({
  trafficarmor_campaign_id: data.trafficarmor_campaign_id || "",
  trafficarmor_campaign_key: data.trafficarmor_campaign_key || "",
  trafficarmor_allowed_hosts: Array.isArray(data.trafficarmor_allowed_hosts)
    ? data.trafficarmor_allowed_hosts.join("\n")
    : data.trafficarmor_allowed_hosts || "",
  trafficarmor_safe_page_html: data.trafficarmor_safe_page_html || "",
});

const getErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  const validationErrors = responseData?.errors;

  if (validationErrors) {
    const firstError = Object.values(validationErrors).flat()[0];
    if (firstError) return firstError;
  }

  return responseData?.message || responseData?.msg || fallback;
};

export default function TrafficArmorSettings() {
  const userToken = storageUtils.get("user_token");
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const querySettings = async () => {
    if (!userToken) {
      setStatus({ tone: "critical", message: "Store login token is missing." });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await g_trafficarmor_settings({ user_token: userToken });
      setSettings(formatSettings(response.data));
    } catch (error) {
      setStatus({
        tone: "critical",
        message: getErrorMessage(error, "Unable to load TrafficArmor settings."),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    querySettings();
    // The store token is fixed for the lifetime of this embedded admin page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (field, value) => {
    setSettings((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setStatus(null);
  };

  const validate = () => {
    const nextErrors = {};
    const campaignId = settings.trafficarmor_campaign_id.trim();
    const campaignKey = settings.trafficarmor_campaign_key.trim();

    if (campaignId && !/^[a-z0-9]{6}$/i.test(campaignId)) {
      nextErrors.trafficarmor_campaign_id =
        "Campaign ID must contain exactly 6 letters or numbers.";
    }

    if (campaignKey && !/^[a-f0-9]{32}$/i.test(campaignKey)) {
      nextErrors.trafficarmor_campaign_key =
        "Campaign key must contain exactly 32 hexadecimal characters.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const saveSettings = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      setStatus(null);

      const response = await u_trafficarmor_settings({
        user_token: userToken,
        trafficarmor_campaign_id: settings.trafficarmor_campaign_id.trim(),
        trafficarmor_campaign_key: settings.trafficarmor_campaign_key.trim(),
        trafficarmor_allowed_hosts:
          settings.trafficarmor_allowed_hosts.trim(),
        trafficarmor_safe_page_html: settings.trafficarmor_safe_page_html,
      });

      setSettings(formatSettings(response.data));
      setStatus({
        tone: "success",
        message: response.msg || "TrafficArmor settings saved.",
      });
    } catch (error) {
      setStatus({
        tone: "critical",
        message: getErrorMessage(error, "Unable to save TrafficArmor settings."),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Layout.AnnotatedSection
        title="TrafficArmor Setting"
        description="Configure the TrafficArmor campaign used by this storefront. If any field is empty, TrafficArmor interception is skipped for this store."
      >
        <LegacyCard sectioned>
          <BlockStack gap="400">
            {status ? (
              <Banner tone={status.tone}>{status.message}</Banner>
            ) : null}

            <TextField
              label="Campaign ID"
              value={settings.trafficarmor_campaign_id}
              onChange={(value) =>
                updateField("trafficarmor_campaign_id", value)
              }
              error={errors.trafficarmor_campaign_id}
              helpText="The 6-character Campaign ID from the TrafficArmor integration package."
              maxLength={6}
              autoComplete="off"
              disabled={loading}
            />

            <TextField
              label="Campaign Key"
              type="password"
              value={settings.trafficarmor_campaign_key}
              onChange={(value) =>
                updateField("trafficarmor_campaign_key", value)
              }
              error={errors.trafficarmor_campaign_key}
              helpText="The 32-character hexadecimal key for this campaign."
              maxLength={32}
              autoComplete="new-password"
              disabled={loading}
            />

            <TextField
              label="Allowed storefront hosts"
              value={settings.trafficarmor_allowed_hosts}
              onChange={(value) =>
                updateField("trafficarmor_allowed_hosts", value)
              }
              error={errors.trafficarmor_allowed_hosts}
              helpText="Enter one host per line or separate hosts with commas, for example: lumorie.top and www.lumorie.top. URLs are normalized automatically."
              multiline={4}
              autoComplete="off"
              disabled={loading}
            />

            <TextField
              label="Safe page HTML"
              value={settings.trafficarmor_safe_page_html}
              onChange={(value) =>
                updateField("trafficarmor_safe_page_html", value)
              }
              helpText="HTML returned to blocked visitors for this store. You can paste a complete HTML document or an HTML fragment."
              multiline={12}
              maxLength={2000000}
              autoComplete="off"
              monospaced
              disabled={loading}
            />

            <InlineStack align="end">
              <Button
                variant="primary"
                loading={saving}
                disabled={loading || !userToken}
                onClick={saveSettings}
              >
                Save TrafficArmor settings
              </Button>
            </InlineStack>
          </BlockStack>
        </LegacyCard>
      </Layout.AnnotatedSection>
    </Layout>
  );
}
